import { Avatar, Box, ButtonBase, Stack, Typography } from '@mui/material'
import { SxProps } from '@mui/system'
import { PropsWithChildren, useMemo } from 'react'
import { Icons } from 'utoniq-ui'

import {
  Maybe,
  RankingUserConnectionFragment,
  RankingUserWithoutIdConnectionFragment,
  User,
} from '@/clients/utoniq-core/schema'
import { Rank } from '@/components/organisms/ranking/Rank'
import { styleSets } from '@/components/styles'
import { useRouter } from '@/core'
import { Cell, Organism, OrganismFrame } from '@/core/architecture'

export type RankingUserDataListItemProps = {
  rankingUser: Maybe<
    RankingUserConnectionFragment | RankingUserWithoutIdConnectionFragment
  >
  user: Maybe<Pick<User, 'id' | 'displayName' | 'avatar'>>
  clickable?: boolean
}

export const RankingUserDataListItem: Organism<RankingUserDataListItemProps> =
  ({ rankingUser, clickable = true, user }) => {
    const { generatePath, routerPages } = useRouter()
    const rankingArrowIcon = useMemo(() => {
      if (!rankingUser?.rank) {
        return <Box sx={{ width: 24 }} />
      }
      if (rankingUser?.previousRank === rankingUser?.rank) {
        return <Icons.ArrowForward sx={{ color: 'white' }} />
      }
      if (
        !rankingUser?.previousRank ||
        rankingUser?.previousRank > rankingUser?.rank
      ) {
        return <Icons.ArrowUpward sx={{ color: 'like.tap' }} />
      }
      return <Icons.ArrowDownward sx={{ color: 'info.dark' }} />
    }, [rankingUser?.previousRank, rankingUser?.rank])

    return (
      <OrganismFrame>
        <ActionAreaCell
          clickable={clickable}
          sx={{
            width: '100%',
            bgcolor: 'grey.100',
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
          }}
          href={
            rankingUser?.userId
              ? generatePath(routerPages.ArtistRanking, {
                  artistId: rankingUser?.userId,
                })
              : undefined
          }
        >
          <Stack
            direction="row"
            flex={1}
            sx={{ minWidth: 0, width: '100%' }}
            spacing={3}
            alignItems="center"
          >
            <Box sx={{ position: 'relative' }}>
              <Rank
                rank={rankingUser?.rank ?? 0}
                size="small"
                sx={{ position: 'absolute', zIndex: 1, top: 6, left: 4 }}
              />
              <Avatar
                sx={{ width: 36, height: 36, ml: '50px' }}
                src={
                  rankingUser?.user?.avatar?.smallUri ??
                  user?.avatar?.smallUri ??
                  ''
                }
              />
            </Box>
            <Stack flex={1} sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                align="left"
                sx={{ ...styleSets.lineCamp(1), width: '100%' }}
              >
                {rankingUser?.user?.displayName ?? user?.displayName}
              </Typography>
            </Stack>
            <Stack
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 3,
                width: 80,
                height: 20,
                position: 'relative',
              }}
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="subtitle1" sx={{ mt: '2px' }}>
                {rankingUser?.point || '---'}
              </Typography>
              <Icons.EventPoint
                sx={{ position: 'absolute', left: -4, top: -2 }}
              />
            </Stack>
            <Stack alignItems="center" sx={{ pr: 3 }}>
              {rankingArrowIcon}
            </Stack>
          </Stack>
        </ActionAreaCell>
      </OrganismFrame>
    )
  }

type ActionAreaCellProps = PropsWithChildren<{
  clickable: boolean
  href?: string
  sx: SxProps
}>
const ActionAreaCell: Cell<ActionAreaCellProps> = ({
  clickable,
  href,
  sx,
  children,
}) => {
  if (clickable && href) {
    return (
      <ButtonBase sx={sx} href={href}>
        {children}
      </ButtonBase>
    )
  }
  return (
    <Stack alignItems="center" justifyContent="center" sx={sx} flex={1}>
      {children}
    </Stack>
  )
}
